import { Button, Card, cn } from "@beweegbaas/ui";
import * as Sentry from "@sentry/tanstackstart-react";
import { Link, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { containerStyles } from "#/components/page";
import { isExpectedApiError, isStaleChunkError } from "#/modules/util.errors/expected";
import { ServerUnavailableError } from "#/modules/util.errors/middleware.server-unavailable";
import { translateApiError } from "#/modules/util.errors/translate";
import { useLocaleParams } from "#/modules/util.helpers/route-href";
import { m } from "#/paraglide/messages";
import { z } from "zod/v4";

interface ErrorBoundaryProps {
  error: unknown;
  /** Route params from the errored route (locale/school); falls back to current route params. */
  params?: { locale?: string; school?: string };
  showLogo?: boolean;
}

/**
 * Users never see the raw JS message as the headline (#98) — always a friendly Dutch line,
 * with the technical message tucked behind a collapsed "Technische details". Chunk-load
 * failures (stale build after a deploy), an unreachable server and a documented API error get
 * their own recognizable text. Stack only in dev.
 */
function parseError(cause: unknown) {
  if (ServerUnavailableError.is(cause)) {
    return {
      message: cause.message,
      details: cause.status ? `HTTP ${cause.status}` : undefined,
      stack: undefined,
    };
  }
  if (isExpectedApiError(cause)) {
    return { message: translateApiError(cause), details: cause.title, stack: undefined };
  }
  const raw = cause instanceof Error ? cause.message : z.string().catch("").parse(cause);

  return {
    message: isStaleChunkError(cause)
      ? m["common.error_boundary_new_version"]()
      : m["common.error_boundary_message"](),
    details: raw || undefined,
    stack: cause instanceof Error && import.meta.env.DEV ? cause.stack : undefined,
  };
}

function HomeLink({
  school,
  params,
}: {
  school?: string;
  params: { locale: string; schoolyear?: string };
}) {
  const logo = (
    <>
      <img src={"/mark-blue.svg"} alt={"Beweeg Baas"} className={"block max-w-10 dark:hidden"} />
      <img src={"/mark-white.svg"} alt={"Beweeg Baas"} className={"hidden max-w-10 dark:block"} />
    </>
  );

  if (school) {
    return (
      <Link to="/$locale/{-$schoolyear}/$school" params={{ ...params, school }}>
        {logo}
      </Link>
    );
  }
  return (
    <Link to="/$locale/{-$schoolyear}" params={params}>
      {logo}
    </Link>
  );
}

export function ErrorBoundaryComponent({ error, params, showLogo = false }: ErrorBoundaryProps) {
  const router = useRouter();
  const routeParams = useLocaleParams();
  const school = params?.school;

  const { message, details, stack } = parseError(error);

  // Server-thrown errors are already captured by the global request/function middleware (start.ts);
  // this covers the client-side ones (component renders, client navigations) that never reach it.
  useEffect(() => {
    if (!error) return;

    Sentry.addBreadcrumb({
      category: "error.boundary",
      level: "error",
      message: "ErrorBoundaryComponent in error-boundary.tsx",
      data: { error },
    });

    if (error instanceof Error) {
      Sentry.captureException(error);
    }
  }, [error]);

  if (stack) {
    return (
      <div
        className={cn(containerStyles({ container: "default" }), "flex h-dvh flex-col gap-10 p-10")}
      >
        <div className="flex grow flex-col gap-2">
          <pre
            className={cn(`w-full grow overflow-x-auto overflow-y-auto
              rounded bg-linear-to-b from-blue-100 to-blue-50
              p-4 dark:from-blue-900/50 dark:to-blue-900/20`)}
          >
            <code>{stack}</code>
          </pre>
        </div>

        <div className="flex items-center justify-between gap-2 pb-4">
          <HomeLink school={school} params={routeParams} />

          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => router.history.back()}>
              <IconMaterialSymbolsArrowLeftAlt />
              {m["common.actions.back"]()}
            </Button>

            <Button
              variant="secondary"
              render={<Link to="/$locale/{-$schoolyear}/logout" params={routeParams} />}
              nativeButton={false}
            >
              <IconMaterialSymbolsLogout />
              {m["common.actions.logout"]()}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main
      className="flex h-full grow flex-col items-start
        bg-linear-to-b px-6 pt-6 dark:from-blue-500 dark:to-blue-400 dark:text-white"
    >
      <div
        className={cn(
          "relative flex grow flex-col gap-10 p-4 pt-16",
          containerStyles({ container: "default" }),
        )}
      >
        {showLogo ? <HomeLink school={school} params={routeParams} /> : null}

        <div className="flex grow flex-col items-center justify-center text-center">
          <div className="mb-10 flex gap-2">
            <Button variant="secondary" onClick={() => router.history.back()}>
              <IconMaterialSymbolsArrowLeftAlt />
              {m["common.actions.back"]()}
            </Button>

            <Button
              variant="secondary"
              onClick={() => {
                window.location.reload();
              }}
            >
              <IconMaterialSymbolsRefresh />
              {m["common.actions.refresh"]()}
            </Button>

            <Button
              variant="secondary"
              render={<Link to="/$locale/{-$schoolyear}/logout" params={routeParams} />}
              nativeButton={false}
            >
              <IconMaterialSymbolsLogout />
              {m["common.actions.logout"]()}
            </Button>
          </div>

          <div className="flex max-w-full flex-col gap-6">
            <div>
              <h1 className="mb-2">{message}</h1>
            </div>
            {details ? (
              <details className="text-sm opacity-60">
                <summary className="cursor-pointer">
                  {m["common.error_boundary_technical_details"]()}
                </summary>
                <Card className="scrollable mt-2 max-w-[90vw] overflow-x-auto text-left">
                  <pre>{details}</pre>
                </Card>
              </details>
            ) : null}
          </div>
        </div>

        <div
          className="error-boundary-image-container mt-10 flex items-end justify-center
            gap-10"
        >
          <img src="/oops-2.svg" alt="oops beweegbaas" className="hidden max-h-[25vh] dark:block" />
          <img src="/oops-1.svg" alt="oops beweegbaas" className="hidden max-h-[25vh] dark:block" />

          <img src="/oops-2-blue.svg" alt="oops beweegbaas" className="max-h-[25vh] dark:hidden" />

          <img src="/oops-1-blue.svg" alt="oops beweegbaas" className="max-h-[25vh] dark:hidden" />
        </div>
      </div>
    </main>
  );
}
